import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { TERMS } from "../../../config/terminology";
import {
   prepareDetailQuotaTable,
   prepareQuotaDetailsTable,
   quotaCopyUrls,
} from "./quotaCopyUtils";

const initialState = {
   isLoading: false,
   isSuccess: false,
   isSuccessMsg: "",
   isInfo: false,
   isInfoMsg: "",
   isError: false,
   isErrorMsg: "",

   countries: [],
   selectedCountries: [],
   selectedPeriodType: "",

   sourcePeriods: [],
   selectedSourcePeriod: "",

   targetPeriods: [],
   selectedTargetPeriod: "",

   quotaDetails: null,
   quotaDetailsTable: null,
   quotaDetailsTableCopy: null,
   selectedTableRow: [],
   selectedTableRows: [],

   detailQuota: null,
   detailQuotaTable: null,

   tableColChecked: false,

   searchText: "",
   
   clearDataValues: false,
   selectedDataCountriesIds: [],
};

export const getUserCountries = createAsyncThunk(
   "quotaCopy/getUserCountries",
   async (obj, thunkAPI) => {
      const data = JSON.parse(localStorage.getItem("data"));
      const token = data.token;
      const url = quotaCopyUrls.userCountries + `userid=${data.userID}`;
      try {
         const response = await fetch(url, {
            method: "GET",
            headers: {
               "Content-Type": "application/json",
               Authorization: `Bearer ${token}`,
               Accept: "application/json",
            },
         });
         const jsonData = await response.json();
         return jsonData;
      } catch (error) {
         console.error("Error: ", error);
      }
   }
);

export const getTargetPeriods = createAsyncThunk(
   "quotaCopy/getTargetPeriods",
   async (obj, thunkAPI) => {
      const data = JSON.parse(localStorage.getItem("data"));
      const token = data.token;
      const { periodType } = obj;
      const url = quotaCopyUrls.targetPeriods + `PType=${periodType}`;
      try {
         const response = await fetch(url, {
            method: "GET",
            headers: {
               "Content-Type": "application/json",
               Authorization: `Bearer ${token}`,
               Accept: "application/json",
            },
         });
         const jsonData = await response.json();
         return jsonData;
      } catch (error) {
         console.error("Error: ", error);
      }
   }
);

export const getSourcePeriods = createAsyncThunk(
   "quotaCopy/getSourcePeriods",
   async (obj, thunkAPI) => {
      const data = JSON.parse(localStorage.getItem("data"));
      const token = data.token;
      const { periodType } = obj;
      const url = quotaCopyUrls.sourcePeriods + `PType=${periodType}`;
      try {
         const response = await fetch(url, {
            method: "GET",
            headers: {
               "Content-Type": "application/json",
               Authorization: `Bearer ${token}`,
               Accept: "application/json",
            },
         });
         const jsonData = await response.json();
         return jsonData;
      } catch (error) {
         console.error("Error: ", error);
      }
   }
);

export const getQuotaDetails = createAsyncThunk(
   "quotaCopy/getQuotaDetails",
   async (obj, thunkAPI) => {
      const data = JSON.parse(localStorage.getItem("data"));
      const token = data.token;
      const url = quotaCopyUrls.quotaDetails;
      try {
         const response = await fetch(url, {
            method: "POST",
            headers: {
               "Content-Type": "application/json",
               Authorization: `Bearer ${token}`,
               Accept: "application/json",
            },
            body: JSON.stringify(obj),
         });
         const jsonData = await response.json();
         return jsonData;
      } catch (error) {
         console.error("Error: ", error);
      }
   }
);

export const getDetailQuota = createAsyncThunk(
   "quotaCopy/getDetailQuota",
   async (obj, thunkAPI) => {
      const data = JSON.parse(localStorage.getItem("data"));
      const token = data.token;
      const {
         quotaID,
         countryID,
         scenarioID,
         validityStartDate,
         validityEndDate,
      } = obj;
      const url =
         quotaCopyUrls.detailQuota +
         `QuotaID=${quotaID}&CountryID=${countryID}&ScenarioID=${scenarioID}&ValidityStartDate=${validityStartDate}&ValidityEndDate=${validityEndDate}`;
      try {
         const response = await fetch(url, {
            method: "GET",
            headers: {
               "Content-Type": "application/json",
               Authorization: `Bearer ${token}`,
               Accept: "application/json",
            },
         });
         const jsonData = await response.json();
         return jsonData;
      } catch (error) {
         console.error("Error: ", error);
      }
   }
);

// post call
export const copyQuota = createAsyncThunk(
   "quotaCopy/copyQuota",
   async (obj, thunkAPI) => {
      const data = JSON.parse(localStorage.getItem("data"));
      const token = data.token;
      const url = quotaCopyUrls.copyQuota;
      try {
         const response = await fetch(url, {
            method: "POST",
            headers: {
               "Content-Type": "application/json",
               Authorization: `Bearer ${token}`,
               Accept: "application/json",
            },
            body: JSON.stringify(obj),
         });
         const jsonData = await response.json();
         return jsonData;
      } catch (error) {
         console.error("Error: ", error);
      }
   }
);

// post call
export const submitQuota = createAsyncThunk(
   "quotaCopy/submitQuota",
   async (obj, thunkAPI) => {
      const data = JSON.parse(localStorage.getItem("data"));
      const token = data.token;
      const url = quotaCopyUrls.submitQuota;
      try {
         const response = await fetch(url, {
            method: "POST",
            headers: {
               "Content-Type": "application/json",
               Authorization: `Bearer ${token}`,
               Accept: "application/json",
            },
            body: JSON.stringify(obj),
         });
         const jsonData = await response.json();
         return jsonData;
      } catch (error) {
         console.error("Error: ", error);
      }
   }
);

export const quotaCopySlice = createSlice({
   name: "quotaCopy",
   initialState,
   reducers: {
      setState: {
         prepare(stateName, stateValue) {
            return {
               payload: { stateName, stateValue },
            };
         },
         reducer(state, action) {
            const { stateName, stateValue } = action.payload;
            state[stateName] = stateValue;
         },
      },
      clearStatus(state, action) {
         state.isSuccess = false;
         state.isSuccessMsg = "";
         state.isInfo = false;
         state.isInfoMsg = "";
         state.isError = false;
         state.isErrorMsg = "";
      }, 
      resetAllStates(state, action) {
         state.isLoading = false;
         state.isSuccess = false;
         state.isSuccessMsg = "";
         state.isInfo = false;
         state.isInfoMsg = "";
         state.isError = false;
         state.isErrorMsg = "";
         state.selectedCountries = [];
         state.selectedPeriodType = "";
         state.sourcePeriods = [];
         state.selectedSourcePeriod = "";
         state.targetPeriods = [];
         state.selectedTargetPeriod = "";
         state.quotaDetails = null;
         state.quotaDetailsTable = null;
         state.quotaDetailsTableCopy = null;
         state.selectedTableRow = [];
         state.selectedTableRows = [];
         state.detailQuota = null;
         state.detailQuotaTable = null;
         state.tableColChecked = false;
         state.searchText = "";
      },
   },
   extraReducers: (builder) => {
      builder
         .addCase(getUserCountries.pending, (state, action) => {
            state.isLoading = true;
         })
         .addCase(getUserCountries.fulfilled, (state, action) => {
            state.isLoading = false;
            state.countries = action.payload;
         })
         .addCase(getUserCountries.rejected, (state, action) => {
            state.isLoading = false;
            console.error("Error from getUserCountries", action.error.message);
         })

         .addCase(getTargetPeriods.pending, (state, action) => {
            state.isLoading = true;
         })
         .addCase(getTargetPeriods.fulfilled, (state, action) => {
            state.targetPeriods = action.payload;
            state.isLoading = false;
         })
         .addCase(getTargetPeriods.rejected, (state, action) => {
            state.isLoading = false;
            console.error("Error from getTargetPeriods", action.error.message);
         })

         .addCase(getSourcePeriods.pending, (state, action) => {
            state.isLoading = true;
         })
         .addCase(getSourcePeriods.fulfilled, (state, action) => {
            state.sourcePeriods = action.payload;
            state.isLoading = false;
         })
         .addCase(getSourcePeriods.rejected, (state, action) => {
            state.isLoading = false;
            console.error("Error from getSourcePeriods", action.error.message);
         })

         .addCase(getQuotaDetails.pending, (state, action) => {
            state.isLoading = true;
         })
         .addCase(getQuotaDetails.fulfilled, (state, action) => {
            state.quotaDetails = action.payload;
            const table = prepareQuotaDetailsTable(action.payload);
            if(state.searchText !== null || state.searchText !== undefined && state.searchText !== ""){
               const updatedList = table.rows.filter((item) =>
                  item.scenarioName.toLowerCase().includes(state.searchText)
               );
               state.quotaDetailsTable =  { rows: updatedList };
            }else{
               state.quotaDetailsTable = table;
            }
            state.quotaDetailsTableCopy = table;
            state.isLoading = false;
            if(state.quotaDetailsTable.rows.length === 0) {
               state.isInfo = true;
               state.isInfoMsg = "No quota found.";
            }
         })
         .addCase(getQuotaDetails.rejected, (state, action) => {
            state.isLoading = false;
            console.error("Error from getQuotaDetails", action.error.message);
         })

         .addCase(getDetailQuota.pending, (state, action) => {
            state.isLoading = true;
         })
         .addCase(getDetailQuota.fulfilled, (state, action) => {
            state.detailQuota = action.payload;
            state.detailQuotaTable = prepareDetailQuotaTable(
               action.payload?.listQuotaCustomers
            );
            state.isLoading = false;
            if (state.detailQuotaTable.rows.length === 0) {
               state.isInfo = true;
               state.isInfoMsg= "No details found."
            }
         })
         .addCase(getDetailQuota.rejected, (state, action) => {
            state.isLoading = false;
            console.error("Error from getDetailQuota", action.error.message);
         })

         .addCase(copyQuota.pending, (state, action) => {
            state.isLoading = true;
         })
         .addCase(copyQuota.fulfilled, (state, action) => {
            if(action.payload) {
               if(typeof action.payload === "string") {
                  state.isError = true;
                  state.isErrorMsg = action.payload;
               }
               else if(action.payload.length === 0) {
                  state.isError = true;
                  state.isErrorMsg = `${TERMS.QUOTA} copying failed!`;
               }
               else {
                  state.isSuccess = true;
                  state.isSuccessMsg = `${TERMS.QUOTA} copied successfully!`;
               }
            }
            else {
               state.isError = true;
               state.isErrorMsg = "No response found!"
            }
            state.isLoading = false;
         })
         .addCase(copyQuota.rejected, (state, action) => {
            state.isLoading = false;
            console.error("Error from submitQuota", action.error.message);
         })

         .addCase(submitQuota.pending, (state, action) => {
            state.isLoading = true;
         })
         .addCase(submitQuota.fulfilled, (state, action) => {
            if(action.payload) {
               if(typeof action.payload === "string") {
                  state.isError = true;
                  state.isErrorMsg = action.payload;
               }
               else if(action.payload.length === 0) {
                  state.isError = true;
                  state.isErrorMsg = `${TERMS.QUOTA} submission failed!`;
               }
               else {
                  state.isSuccess = true;
                  state.isSuccessMsg = `${TERMS.QUOTA} submitted successfully!`;
               }
            }
            else {
               state.isError = true;
               state.isErrorMsg = "No response found!"
            }
            state.isLoading = false;
         })
         .addCase(submitQuota.rejected, (state, action) => {
            state.isLoading = false;
            console.error("Error from submitQuota", action.error.message);
         });
   },
});

export const { setState, clearStatus, resetAllStates } = quotaCopySlice.actions;
export default quotaCopySlice.reducer;
